import { useDrag } from '@use-gesture/react'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { animated, useSpring } from 'react-spring'

import { ChatTypeButton } from './chat-type-button'

export interface ChatConfig {
  type: string
  label: React.ReactNode
  title?: string
  unreadCount?: number
}

let dragTimeThreshold = 200

export interface ChatTypeSelectorProps {
  value: string
  options: ChatConfig[]
  onChange: (value: string) => void
}

export function ChatTypeSelector ({ value, options, onChange }: ChatTypeSelectorProps): JSX.Element {
  let ref = useRef<HTMLDivElement>(null)
  let widths = useRef<Map<string, number>>(new Map())
  let offsets = useRef<Map<string, number>>(new Map())

  let g = useCallback((value: string) => ({
    transform: `translateX(${offsets.current.get(value) ?? 0}px) scaleX(${widths.current.get(value) ?? 0})`
  }), [])

  let [ barStyle, barSpringRef ] = useSpring(g(value), [ value ])
  let [{ x }, draggableSpringRef ] = useSpring(() => ({ x: 0 }))

  let startDragX = useRef(0)
  let endDragAt = useRef(Date.now())

  let bind = useDrag(({ down, first, elapsedTime, last, movement: [ mx, my ], event }) => {
    if (first) {
      startDragX.current = ref.current?.scrollLeft ?? 0
    }

    if (elapsedTime > dragTimeThreshold && last) {
      endDragAt.current = Date.now()
    }

    let x = Math.max(0, startDragX.current - mx)
    draggableSpringRef.start({ x, immediate: down })
  })

  useLayoutEffect(() => {
    barSpringRef.start(g(value))
  }, [ barSpringRef, g, value ])

  return (
    <animated.div
      ref={ref}
      className='relative flex flex-row pl-2 items-start flex-grow max-w-full overflow-x-scroll overflow-y-hidden no-scrollbar border-pink-400 touch-none'
      scrollLeft={x}
      {...bind()}
    >
      {options.map((chat, i) => (
        <ChatTypeButton
          key={chat.type}
          isActive={chat.type === value}
          title={chat.title}
          unreadCount={chat.unreadCount}
          onLayout={el => {
            widths.current.set(chat.type, el.getBoundingClientRect().width - 16)
            offsets.current.set(chat.type, el.offsetLeft + 8)

            barSpringRef.start(g(value))
          }}
          onClick={() => {
            if (Date.now() - endDragAt.current < dragTimeThreshold) {
              return
            }

            onChange(chat.type)
          }}
        >
          {chat.label}
        </ChatTypeButton>
      ))}

      <animated.i
        className='absolute block left-0 bottom-0 w-[1px] h-[2px] bg-blue-600 origin-left will-change-transform'
        style={barStyle}
      />
    </animated.div>
  )
}
